import React, { useEffect, useState } from "react";
import { Loader2, Archive, RotateCcw, Package } from "lucide-react";
import { toast } from "sonner";
import api from "./api";

/* Archived Orders tab (Aug 2026). One place for every order marked archived,
   from any source. These never show in customer accounts. */

const AUBERGINE = "#5A4565";
const CREAM = "#FBF6EA";
const EDGE = "#EFE4CC";
const BERRY = "#6E1E3A";

export default function ConsoleArchivedOrders() {
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    api.get("/console/archived-orders")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Couldn't load archived orders."));
  };
  useEffect(load, []);

  const restore = async (id) => {
    if (!window.confirm("Restore this order to the live pipeline? It will reappear in admin lists and in the customer's account.")) return;
    setBusyId(id);
    try {
      const r = await api.post(`/console/archived-orders/${id}/restore`);
      toast.success(`Order restored to ${r.data.restored_to.replace("_", " ")}.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't restore.");
    } finally { setBusyId(null); }
  };

  if (!data) return <div className="p-6"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  const rows = [
    ...(data.console || []).map((o) => ({
      id: o.id, source: "console",
      name: o.customer_name || o.email, email: o.email,
      label: (o.line_items || [])[0]?.name || `Ref ${String(o.id || "").slice(0, 8)}`,
      extra: (o.line_items || []).length > 1 ? `+${o.line_items.length - 1} more` : "",
      total: Number(o.total || 0), archived_at: o.archived_at, reason: o.archive_reason || "",
      restorable: true,
    })),
    ...(data.manual || []).map((m) => ({
      id: m.id, source: "manual",
      name: m.customer_name || m.email, email: m.email,
      label: (m.items || "").split("\n")[0]?.slice(0, 50) || "Manual order",
      extra: "", total: Number(m.amount || 0), archived_at: m.archived_at, reason: "legacy audit",
      restorable: true,
    })),
  ];

  return (
    <div data-testid="console-archived-orders" className="space-y-5" style={{ color: AUBERGINE }}>
      <header>
        <div className="text-[10px] tracking-[0.45em] uppercase font-semibold" style={{ color: BERRY }}>✦ Out of the pipeline</div>
        <h1 className="text-4xl mt-1 leading-tight">Archived Orders</h1>
        <p className="italic mt-1 text-sm opacity-70">
          Every archived order in one place ({data.total}). Customers never see these. Restore one if it was archived by mistake.
        </p>
      </header>

      {rows.length === 0 ? (
        <div className="bg-white border rounded-md p-10 text-center italic opacity-60" style={{ borderColor: EDGE }} data-testid="archived-empty">
          <Archive className="w-8 h-8 mx-auto mb-3 opacity-40" />
          Nothing in the archive.
        </div>
      ) : (
        <div className="bg-white border rounded-md overflow-x-auto" style={{ borderColor: EDGE }}>
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="text-left text-[10px] tracking-[0.25em] uppercase" style={{ backgroundColor: CREAM }}>
                <th className="px-4 py-2.5">Order</th>
                <th className="px-3 py-2.5">Customer</th>
                <th className="px-3 py-2.5">Total</th>
                <th className="px-3 py-2.5">Archived</th>
                <th className="px-3 py-2.5">Reason</th>
                <th className="px-3 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.source}-${r.id}`} className="border-t" style={{ borderColor: EDGE }} data-testid={`archived-row-${r.id}`}>
                  <td className="px-4 py-2.5">
                    <div className="font-semibold inline-flex items-center gap-1.5">
                      <Package className="w-3.5 h-3.5 opacity-50" /> {r.label}
                      {r.extra && <span className="opacity-50 font-normal text-xs">{r.extra}</span>}
                    </div>
                    <div className="text-[10px] opacity-50 mt-0.5">#{String(r.id).slice(0, 8)} · {r.source}</div>
                  </td>
                  <td className="px-3 py-2.5">
                    <div>{r.name}</div>
                    <div className="text-[10px] opacity-50">{r.email}</div>
                  </td>
                  <td className="px-3 py-2.5">${r.total.toFixed(2)}</td>
                  <td className="px-3 py-2.5 text-xs opacity-70">{r.archived_at ? new Date(r.archived_at).toLocaleDateString("en-NZ") : "unknown"}</td>
                  <td className="px-3 py-2.5 text-xs opacity-70">{r.reason || ""}</td>
                  <td className="px-3 py-2.5 text-right">
                    <button
                      type="button"
                      onClick={() => restore(r.id)}
                      disabled={busyId === r.id}
                      data-testid={`archived-restore-${r.id}`}
                      className="px-2.5 py-1.5 rounded border text-[10px] tracking-[0.15em] uppercase font-semibold inline-flex items-center gap-1"
                      style={{ borderColor: AUBERGINE, color: AUBERGINE }}
                    >
                      {busyId === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />} Restore
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
